// Веб-кабинет покупателя (макет «Профиль»): история просмотров, «Мои
// покупки», возвраты/споры и отзывы. Схемы и http-реализация живут отдельно
// от types.ts/http.ts, чтобы не раздувать общий файл: в `Api` это пространство
// `api.cabinet.*`, мок — в ./mock/cabinet.ts.
import { z } from "zod";
import { BuyerCouponsSchema, type BuyerCoupons } from "@osmix/contracts/promo-partners";
export type { BuyerCoupon, BuyerCoupons, BuyerCouponHistoryItem, CartPromoCard } from "@osmix/contracts/promo-partners";

// ── История просмотров ─────────────────────────────────────────────────────
export const ViewHistoryEntry = z.object({
	productId: z.string(),
	name: z.string(),
	brand: z.string().nullish(),
	price: z.number(),
	image: z.string().nullish(),
	viewedAt: z.string(),
});
export type ViewHistoryEntry = z.infer<typeof ViewHistoryEntry>;

// ── Мои покупки ────────────────────────────────────────────────────────────
export const BuyerPurchaseStatus = z.enum(["delivered", "return_pending", "returned"]);
export type BuyerPurchaseStatus = z.infer<typeof BuyerPurchaseStatus>;

export const BuyerPurchase = z.object({
	orderId: z.string(),
	// Короткий номер заказа (123456) — его и показываем вместо id (правка №13).
	orderNumber: z.number().nullish(),
	orderCreatedAt: z.string(),
	orderTotal: z.number(),
	orderLineId: z.string(),
	productId: z.string(),
	shopId: z.string().nullish(),
	name: z.string(),
	brand: z.string(),
	photo: z.string().nullish(),
	variant: z.string().nullish(),
	size: z.string().nullish(),
	color: z.string().nullish(),
	quantity: z.number(),
	unitPrice: z.number(),
	status: BuyerPurchaseStatus,
	statusAt: z.string().nullish(),
	returnableQuantity: z.number(),
	returnId: z.string().nullish(),
	canDispute: z.boolean(),
});
export type BuyerPurchase = z.infer<typeof BuyerPurchase>;

// ── Возвраты ───────────────────────────────────────────────────────────────
// «other» — «Другое» с обязательным комментарием (правка №14, Элина 16.10.2026).
export const ReturnReasonCode = z.enum(["not_suitable", "defect", "wrong_or_incomplete", "damaged", "other"]);
export type ReturnReasonCode = z.infer<typeof ReturnReasonCode>;

export const RETURN_REASON_LABEL: Record<ReturnReasonCode, string> = {
	not_suitable: "Товар в порядке, но не подошел",
	defect: "Брак",
	wrong_or_incomplete: "Пришло не то или не всё",
	damaged: "Повреждения, следы использования",
	other: "Другое",
};

/** Причина, для которой комментарий обязателен: без слов «Другое» продавцу ничего не говорит. */
export const reasonNeedsComment = (code: ReturnReasonCode | null | undefined) => code === "other";

export const ReturnAttachment = z.object({
	id: z.string(),
	returnId: z.string().nullish(),
	lineId: z.string().nullish(),
	author: z.enum(["buyer", "seller"]),
	kind: z.enum(["photo", "video"]),
	url: z.string(),
	thumbnailUrl: z.string().nullish(),
	durationSec: z.number().nullish(),
	createdAt: z.string(),
});
export type ReturnAttachment = z.infer<typeof ReturnAttachment>;

export const ReturnableLine = z.object({
	orderLineId: z.string(),
	productId: z.string(),
	name: z.string(),
	photo: z.string().nullish(),
	variant: z.string().nullish(),
	orderedQuantity: z.number(),
	availableQuantity: z.number(),
	unitPrice: z.number(),
	// Индивидуальный заказ: причина «не подошёл» недоступна (fd-request-14).
	customized: z.boolean().optional(),
});
export type ReturnableLine = z.infer<typeof ReturnableLine>;

export const ReturnableOrder = z.object({
	orderId: z.string(),
	returnWindowEndsAt: z.string().nullish(),
	lines: z.array(ReturnableLine),
});
export type ReturnableOrder = z.infer<typeof ReturnableOrder>;

export const ReturnLine = z.object({
	id: z.string(),
	orderLineId: z.string(),
	productId: z.string(),
	name: z.string(),
	// Бренд позиции из снимка заказа (шлюз отдаёт с 3084:44025); в карточке
	// позиции стоит он, а не название магазина.
	brand: z.string().nullish(),
	photo: z.string().nullish(),
	variant: z.string().nullish(),
	quantity: z.number(),
	unitPrice: z.number(),
	refund: z.number(),
	reasonCode: ReturnReasonCode,
	comment: z.string(),
	cancelledAt: z.string().nullish(),
	attachments: z.array(ReturnAttachment),
});
export type ReturnLine = z.infer<typeof ReturnLine>;

export const BuyerReturnUiStatus = z.enum(["under_review", "awaiting_shipment", "returned", "dispute_open", "dispute_closed"]);
export type BuyerReturnUiStatus = z.infer<typeof BuyerReturnUiStatus>;

export const BuyerReturnDetail = z.object({
	id: z.string(),
	orderId: z.string(),
	orderNumber: z.number().nullish(),
	shopId: z.string().nullish(),
	shopName: z.string().nullish(),
	kind: z.enum(["return", "dispute"]),
	reason: z.string(),
	status: z.enum(["requested", "approved", "rejected", "received", "refunded"]),
	uiStatus: BuyerReturnUiStatus,
	dueAt: z.string().nullish(),
	resolvedAt: z.string().nullish(),
	resolutionNote: z.string().nullish(),
	refundTotal: z.number(),
	requestedRefund: z.number(),
	reasonCode: ReturnReasonCode.nullish(),
	goodsReceivedAt: z.string().nullish(),
	refundedAt: z.string().nullish(),
	createdAt: z.string(),
	updatedAt: z.string(),
	lines: z.array(ReturnLine),
	attachments: z.array(ReturnAttachment),
	shipment: z
		.object({
			deadlineAt: z.string(),
			address: z.string(),
			carrier: z.string(),
			refundTargetLabel: z.string(),
		})
		.nullish(),
	dispute: z
		.object({
			id: z.string(),
			status: z.enum(["open", "closed"]),
			buyerComment: z.string(),
			sellerText: z.string().nullish(),
			sellerRespondedAt: z.string().nullish(),
			outcome: z.enum(["buyer", "seller", "partial"]).nullish(),
			supportThreadId: z.string().nullish(),
			openedAt: z.string(),
			closedAt: z.string().nullish(),
		})
		.nullish(),
});
export type BuyerReturnDetail = z.infer<typeof BuyerReturnDetail>;
export type BuyerReturnTab = "active" | "dispute" | "archive";

export type CreateReturnInput = {
	orderId: string;
	lines: Array<{
		orderLineId: string;
		quantity: number;
		reason: ReturnReasonCode;
		comment?: string;
		attachmentIds?: string[];
	}>;
};

// ── Отзывы ─────────────────────────────────────────────────────────────────
export const ReviewMedia = z.object({ url: z.string(), kind: z.enum(["photo", "video"]) });
export type ReviewMedia = z.infer<typeof ReviewMedia>;

export const BuyerReview = z.object({
	id: z.string(),
	orderId: z.string(),
	productId: z.string().nullish(),
	productName: z.string().nullish(),
	productPhoto: z.string().nullish(),
	brand: z.string().nullish(),
	rating: z.number(),
	text: z.string(),
	variant: z.string().nullish(),
	anonymous: z.boolean(),
	media: z.array(ReviewMedia).default([]),
	sellerReply: z.string().nullish(),
	/** Название магазина, которым подписан ответ. */
	sellerReplyAuthor: z.string().nullish(),
	createdAt: z.string(),
});
export type BuyerReview = z.infer<typeof BuyerReview>;

export const BuyerPendingReview = z.object({
	productId: z.string(),
	orderId: z.string(),
	name: z.string(),
	brand: z.string(),
	price: z.number(),
	image: z.string().nullish(),
	variant: z.string().nullish(),
	size: z.string().nullish(),
	orderedAt: z.string(),
});
export type BuyerPendingReview = z.infer<typeof BuyerPendingReview>;

export type CreateReviewInput = {
	productId: string;
	orderId?: string;
	rating: number;
	text?: string;
	anonymous?: boolean;
	media?: ReviewMedia[];
};

// ── Поверхность api.cabinet ────────────────────────────────────────────────
export type CabinetApi = {
	viewHistory(limit?: number): Promise<ViewHistoryEntry[]>;
	purchases(): Promise<BuyerPurchase[]>;
	returnable(orderId: string): Promise<ReturnableOrder>;
	uploadReturnAttachment(file: File): Promise<ReturnAttachment>;
	createReturn(input: CreateReturnInput): Promise<void>;
	returns(tab: BuyerReturnTab): Promise<BuyerReturnDetail[]>;
	returnDetail(id: string): Promise<BuyerReturnDetail>;
	// keepLineIds пуст — полная отмена; иначе отменяются остальные позиции.
	// reduce — у оставленных позиций снять N штук (частичная отмена по количеству).
	cancelReturn(id: string, keepLineIds: string[], reduce?: Array<{ lineId: string; quantity: number }>): Promise<BuyerReturnDetail>;
	openDispute(id: string, input: { comment: string; attachmentIds?: string[] }): Promise<BuyerReturnDetail>;
	closeDispute(id: string): Promise<BuyerReturnDetail>;
	reviews(): Promise<BuyerReview[]>;
	pendingReviews(): Promise<BuyerPendingReview[]>;
	uploadReviewMedia(file: File): Promise<ReviewMedia>;
	createReview(input: CreateReviewInput): Promise<BuyerReview>;
	/** «Мои купоны» (fd-promo-34): доступные коды и история использования. */
	coupons(): Promise<BuyerCoupons>;
	saveCoupon(code: string): Promise<BuyerCoupons>;
	removeCoupon(code: string): Promise<BuyerCoupons>;
};

type Requester = <T>(path: string, opts?: { method?: string; body?: unknown }) => Promise<T>;

const enc = encodeURIComponent;

// Списки валидируем поштучно: один битый элемент не должен ронять раздел.
const parseList = <T>(schema: z.ZodType<T>, raw: unknown, label: string): T[] => {
	if (!Array.isArray(raw)) return [];
	const out: T[] = [];
	for (const item of raw) {
		const parsed = schema.safeParse(item);
		if (parsed.success) out.push(parsed.data);
		else console.warn(`[api] cabinet.${label}: skipped invalid item`, parsed.error.issues[0]);
	}
	return out;
};

const upload = (file: File) => {
	const form = new FormData();
	form.append("file", file);
	return form;
};

export const createCabinetHttp = (request: Requester): CabinetApi => ({
	viewHistory: async (limit = 50) => parseList(ViewHistoryEntry, await request(`/buyer/view-history?limit=${limit}`), "viewHistory"),
	purchases: async () => parseList(BuyerPurchase, await request("/buyer/purchases"), "purchases"),
	returnable: async (orderId) => ReturnableOrder.parse(await request(`/buyer/orders/${enc(orderId)}/returnable`)),
	uploadReturnAttachment: async (file) =>
		ReturnAttachment.parse(await request("/buyer/returns/attachments", { method: "POST", body: upload(file) })),
	createReturn: async (input) => {
		await request("/buyer/returns", { method: "POST", body: input });
	},
	returns: async (tab) => parseList(BuyerReturnDetail, await request(`/buyer/returns?tab=${tab}`), "returns"),
	returnDetail: async (id) => BuyerReturnDetail.parse(await request(`/buyer/returns/${enc(id)}`)),
	cancelReturn: async (id, keepLineIds, reduce) =>
		BuyerReturnDetail.parse(
			await request(`/buyer/returns/${enc(id)}/cancel`, {
				method: "POST",
				body: { keepLineIds, reduce },
			}),
		),
	openDispute: async (id, input) =>
		BuyerReturnDetail.parse(await request(`/buyer/returns/${enc(id)}/dispute`, { method: "POST", body: input })),
	closeDispute: async (id) => BuyerReturnDetail.parse(await request(`/buyer/returns/${enc(id)}/dispute/close`, { method: "POST" })),
	reviews: async () => parseList(BuyerReview, await request("/buyer/reviews"), "reviews"),
	pendingReviews: async () => parseList(BuyerPendingReview, await request("/buyer/reviews/pending"), "pendingReviews"),
	uploadReviewMedia: async (file) => ReviewMedia.parse(await request("/buyer/reviews/media", { method: "POST", body: upload(file) })),
	createReview: async (input) => BuyerReview.parse(await request("/buyer/reviews", { method: "POST", body: input })),
	coupons: async () => BuyerCouponsSchema.parse(await request("/buyer/coupons")),
	saveCoupon: async (code) => BuyerCouponsSchema.parse(await request("/buyer/coupons", { method: "POST", body: { code } })),
	removeCoupon: async (code) => BuyerCouponsSchema.parse(await request(`/buyer/coupons/${enc(code)}`, { method: "DELETE" })),
});
